/**
 * Page sharing dialog: per-organization toggle of the user.
 * A shared page (and its whole subtree + blocks + attachments) starts
 * syncing with all members of the organization.
 */
import { createSignal, For, Show } from "solid-js";
import { Check, Link2, Loader2, Users } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { canonicalUrl } from "@/lib/canonical-link";
import { usePageSharing } from "../hooks/use-page-sharing";

interface PageShareDialogProps {
  pageId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PageShareDialog(props: PageShareDialogProps) {
  const { t } = useI18n();
  const sharing = usePageSharing(() => props.pageId, { enabled: () => props.open });
  const [copied, setCopied] = createSignal(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(canonicalUrl());
      setCopied(true);
      showToast(t(msg`Link copied`), "success");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast(t(msg`Couldn't copy the link`), "error");
    }
  };

  const handleToggle = async (organizationId: string) => {
    try {
      await sharing.toggleShare(organizationId);
    } catch (error) {
      console.error("Error toggling page share:", error);
      showToast(t(msg`Could not update sharing. Please try again.`), "error");
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md p-0">
      <DialogHeader size="md">
        <div class="flex items-center gap-3">
          <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Users class="h-5 w-5" />
          </div>
          <DialogTitle class="text-xl font-bold">{t(msg`Share page`)}</DialogTitle>
        </div>
      </DialogHeader>

      <div class="space-y-4 p-6">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleCopyLink}
          class="w-full flex items-center justify-center gap-2"
          data-testid="copy-link-button"
        >
          <Show when={copied()} fallback={<Link2 class="h-4 w-4" />}>
            <Check class="h-4 w-4 text-emerald-500" />
          </Show>
          <span>{copied() ? t(msg`Link copied`) : t(msg`Copy link`)}</span>
        </Button>

        <DialogDescription class="text-sm text-muted-foreground">
          {t(
            msg`Members of the selected organizations will be able to view and edit this page, its subpages and attachments.`,
          )}
        </DialogDescription>

        <Show
          when={!sharing.organizations.isLoading}
          fallback={
            <div class="flex justify-center py-6">
              <Loader2 class="h-5 w-5 animate-spin text-muted-foreground/50" />
            </div>
          }
        >
          <Show
            when={(sharing.organizations.data?.length ?? 0) > 0}
            fallback={
              <p class="py-4 text-sm text-muted-foreground/70">
                {t(msg`You don't belong to any organization yet. Create one to share pages.`)}
              </p>
            }
          >
            <ul class="space-y-2">
              <For each={sharing.organizations.data ?? []}>
                {(org) => (
                  <li class="flex items-center justify-between gap-3 rounded-xl border border-border/40 px-4 py-3">
                    <span class="truncate text-sm font-medium">{org.name}</span>
                    <Switch
                      checked={sharing.isShared(org.id)}
                      disabled={sharing.isToggling() !== null}
                      onCheckedChange={() => {
                        void handleToggle(org.id);
                      }}
                      aria-label={t(msg`Share with this organization`)}
                    />
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </Show>
      </div>
    </Dialog>
  );
}
